import { Children, Fragment, isValidElement, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { ClusterItem } from 'admination-react'

const EXIT_MS = 180

function staggerWrap(content: ReactNode, phase: 'enter' | 'exit'): ReactNode {
  if (content == null || content === false) return null
  const items =
    isValidElement(content) && content.type === Fragment
      ? Children.toArray((content.props as { children?: ReactNode }).children)
      : Children.toArray(content)
  return (
    <>
      {items.map((item, i) => {
        const key = isValidElement(item) && item.key != null ? item.key : i
        return (
          <ClusterItem key={key} phase={phase} index={i}>
            {item}
          </ClusterItem>
        )
      })}
    </>
  )
}

function isEmpty(content: ReactNode): boolean {
  return content == null || content === false
}

export type ClusterSwap = {
  node: ReactNode
  isShown: boolean
}

export function useClusterSwap(content: ReactNode, id: unknown): ClusterSwap {
  const [display, setDisplay] = useState<{ id: unknown; content: ReactNode; phase: 'enter' | 'exit' }>(() => ({
    id,
    content,
    phase: 'enter',
  }))
  const pendingRef = useRef<{ id: unknown; content: ReactNode } | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => () => clearTimeout(timerRef.current), [])

  useEffect(() => {
    if (id === display.id) {
      if (display.phase === 'exit' && pendingRef.current) {
        pendingRef.current = { id, content }
      } else if (display.content !== content) {
        setDisplay((d) => ({ ...d, content }))
      }
      return
    }
    if (pendingRef.current?.id === id) {
      pendingRef.current = { id, content }
      return
    }
    if (isEmpty(display.content)) {
      pendingRef.current = null
      clearTimeout(timerRef.current)
      setDisplay({ id, content, phase: 'enter' })
      return
    }
    pendingRef.current = { id, content }
    setDisplay((d) => ({ ...d, phase: 'exit' }))
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(
      () => {
        const next = pendingRef.current
        pendingRef.current = null
        if (next) setDisplay({ id: next.id, content: next.content, phase: 'enter' })
      },
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : EXIT_MS,
    )
  }, [id, content, display.id, display.phase, display.content])

  return { node: staggerWrap(display.content, display.phase), isShown: !isEmpty(display.content) }
}
